import { Token } from '../models/token.model';
import { inject, Injectable } from '@angular/core';
import { ILoginRequest } from '../dtos/requests/login.request';
import { ErrorOrError } from '../../../../core/results/error-or/models/error-or-error.model';
import { handleApiErrors } from '../../../../core/data-access/http-client/interceptors/error.interceptor';
import { IToken } from '../dtos/responses/token.response';
import { Router } from '@angular/router';
import { invalidCredentialsError } from '../../../common/errors/login.errors';
import { UserContextService } from './user-context.service';
import { CurrentUsersApiClient } from '../../../account-management/users/clients/current-users-api.client';
import { IUserContext } from '../models/user-context.model';
import { switchMap } from 'rxjs';
import { IIdentityAbiClient } from '../clients/identity-api-client.interface';
import { IdentityApiClientProvider } from '../clients/identity-api-client-provider';
import { ToastService } from '../../../../../trash/shared/components/toasters/Toast.service';
import { UserUiServiceV2 } from '../../../../../shared';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  identityApiClient: IIdentityAbiClient = inject(IdentityApiClientProvider).getClient();
  currentUsersApiClient = inject(CurrentUsersApiClient);
  toastService = inject(ToastService);
  router = inject(Router);

  authenticate(request: ILoginRequest, isPersist: boolean = false): void {
    this.identityApiClient
      .login(request)
      .pipe(
        switchMap((token) => {
          this.handleLoginSuccess(token, isPersist);
          return this.currentUsersApiClient.get();
        })
      )
      .subscribe({
        next: (userContext) => this.handleUserContextSuccess(userContext),
        error: (errors: ErrorOrError[]) => {
          handleApiErrors(errors, this.toastService, [invalidCredentialsError]);
          this.logout();
        }
      });
  }

  ensureAuthenticated(): Token | undefined {
    const token = UserContextService.getToken();

    if (!UserContextService.isAuthenticated()) {
      UserContextService.remove();
      return undefined;
    }

    if (token?.isAccessTokenExpired()) {
      this.refreshAccessToken(token);
      return token;
    }

    return token;
  }

  refreshAccessToken(token: Token): void {
    this.identityApiClient
      .refresh(token.toRefreshTokenRequest())
      .pipe(
        switchMap((token) => {
          UserContextService.setToken(token, token.isPersisted);
          return this.currentUsersApiClient.get();
        })
      )
      .subscribe({
        next: (userContext) => UserContextService.setUserStorage(userContext),
        error: (errors: ErrorOrError[]) => handleApiErrors(errors, this.toastService)
      });
  }

  logout(): void {
    UserContextService.remove();
    this.router.navigate(['auth/login']);
  }

  handleLoginSuccess(token: IToken, isPersist: boolean): void {
    UserContextService.setToken(token, isPersist);
  }

  handleUserContextSuccess(userContext: IUserContext): void {
    UserContextService.setUserStorage(userContext);
    this.toastService.showSuccess('Login Successful', 'You have been logged in successfully. Welcome back!');
    this.router.navigate([UserUiServiceV2.getInitialPage()]);
  }
}
